import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { deleteTransactionFromGroup, updateTransactionFromGroup } from "features/groups/api/Group";
import { useTranslation } from "shared/hooks/useTranslation";
import { Group } from "./useGroupDetails";
import { GroupTransaction } from "@/types/GroupTransaction";

/**
 * Hook for managing group transactions locally with optimistic UI updates.
 * Handles add, edit, delete operations for group transactions.
 * @param language - App locale (en/es) for ML categorization when category is undefined
 * @param onTransactionsChange - Called with the updated transactions array after save/delete/add so parent can sync (e.g. update group balances).
 */
export function useTransactionsLocal(
    group: Group,
    transactions: GroupTransaction[],
    language?: string,
    onTransactionsChange?: (transactions: GroupTransaction[]) => void
) {
    const { t } = useTranslation();
    
    const [items, setItems] = useState<GroupTransaction[]>(transactions);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [savingId, setSavingId] = useState<string | null>(null);

    // Keep in sync when transactions prop changes
    useEffect(() => {
        setItems(transactions ?? []);
    }, [transactions]);

    const idKey = useMemo(() => (transaction: GroupTransaction) => (transaction?._id as string) ?? (transaction?.name as string), []);
    const onEditClick = (tx: GroupTransaction) => setEditingId(idKey(tx));
    const onCancelEdit = () => setEditingId(null);

    const handleSaveTransaction = async (updatedTransaction: GroupTransaction) => {
        const id = idKey(updatedTransaction);
        
        // Prevent double calls - if already saving this transaction, ignore
        if (savingId === id) {
            console.log('Transaction already being saved, ignoring duplicate call');
            return;
        }
        
        try {
            setSavingId(id);
            const updatedGroup = await updateTransactionFromGroup(group._id, updatedTransaction, language);
            const savedTransaction = updatedGroup.transactions?.find((tx) => idKey(tx) === id) ?? updatedTransaction;

            // Optimistic UI update - immutable
            setItems((prev) => {
                const idx = prev.findIndex((t) => idKey(t) === id);
                const next = idx === -1 ? [savedTransaction, ...prev] : (() => {
                    const arr = prev.slice();
                    arr[idx] = { ...prev[idx], ...savedTransaction };
                    return arr;
                })();
                onTransactionsChange?.(next);
                return next;
            });

            setEditingId(null);
            toast.success(t("transactions.transactionUpdated", { defaultValue: "Transaction updated" }));
        } catch (err) {
            console.error(err);
            toast.error(t("transactions.transactionNotUpdated", { defaultValue: "Could not update transaction" }));
        } finally {
            setSavingId(null);
        }
    };

    const handleDeleteTransaction = async (transaction: GroupTransaction) => {
        const id = idKey(transaction);
        try {
            setSavingId(id);
            const response = await deleteTransactionFromGroup(group._id, transaction);

            // Optimistic UI update
            setItems((prev) => {
                const next = response.group?.transactions ?? prev.filter((t) => idKey(t) !== id);
                onTransactionsChange?.(next);
                return next;
            });

            if (editingId === id) setEditingId(null);
            toast.success(t("transactions.transactionDeleted", { defaultValue: "Transaction deleted" }));
        } catch (err) {
            console.error(err);
            toast.error(t("transactions.transactionNotDeleted", { defaultValue: "Could not delete transaction" }));
        } finally {
            setSavingId(null);
        }
    };

    const handleAddTransaction = async (transaction: GroupTransaction) => {
        const id = idKey(transaction);
        try {
            setSavingId(id);
            setItems((prev) => {
                const next = [transaction, ...prev];
                onTransactionsChange?.(next);
                return next;
            });

            setEditingId(null);
            toast.success(t("transactions.transactionAdded"));
        } catch (err) {
            console.error(err);
            toast.error(t("transactions.transactionNotAdded"));
        } finally {
            setSavingId(null);
        }
    };

    return {
        items,
        editingId,
        savingId,
        onEditClick,
        onCancelEdit,
        handleSaveTransaction,
        handleAddTransaction,
        handleDeleteTransaction
    };
}
